import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, Loader2, Search, X, Upload, Download, Trash2, Filter, FileText,
  Phone, Mail, Building2, Star, TicketIcon, Calendar, Paperclip,
  ChevronRight, User as UserIcon, DollarSign, Tag, Clock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  listLeads, getLead, createLead, updateLead, deleteLead, leadStats,
  addLeadNote, addLeadFollowup, updateLeadFollowup, deleteLeadFollowup,
  addLeadFile, deleteLeadFile, importLeads, parseLeadsCsv, convertLeadToTicket,
  leadsCsvTemplateUrl, leadsCsvExportUrl, listUsers, listDepartments,
  uploadFile,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const STAGES = ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost'];
const STAGE_META = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  contacted: { label: 'Contacted', color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  qualified: { label: 'Qualified', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  proposal: { label: 'Proposal', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  won: { label: 'Won', color: 'bg-emerald-600 text-white', dot: 'bg-emerald-500' },
  lost: { label: 'Lost', color: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
};

const SOURCE_META = {
  website: { label: 'Website', color: 'bg-cyan-50 text-cyan-700' },
  import: { label: 'Import', color: 'bg-slate-100 text-slate-700' },
  manual: { label: 'Manual', color: 'bg-emerald-50 text-emerald-700' },
  referral: { label: 'Referral', color: 'bg-amber-50 text-amber-700' },
  email: { label: 'Email', color: 'bg-indigo-50 text-indigo-700' },
};

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtCurrency = (v, ccy = 'USD') => {
  if (v == null || v === '') return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(v); }
  catch { return `${ccy} ${v}`; }
};

const LeadsPage = () => {
  const { user } = useAuth();
  const [view, setView] = useState('list'); // 'list' | 'kanban'
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ stage: '', owner_id: '', source: '', q: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const userLookup = useMemo(() => {
    const m = {}; users.forEach((u) => { m[u.user_id] = u; }); return m;
  }, [users]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [r, s] = await Promise.all([
        listLeads(params),
        leadStats().catch(() => null),
      ]);
      setLeads(r.data || []);
      setStats(s);
    } catch {
      toast({ title: 'Failed to load leads', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    listUsers().then((r) => setUsers(r.data || [])).catch(() => {});
    listDepartments().then((r) => setDepts(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const onCreated = async (l) => {
    setShowNew(false);
    await reload();
    setSelectedId(l.id);
  };

  return (
    <div data-testid="leads-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">CRM · Sales Pipeline</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Leads
          </h1>
          <p className="mt-1.5 text-sm text-[#0b1a2e]/60">
            Every prospect, from website enquiry to closed deal. Import in bulk, assign to staff, and follow up on the calendar.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowImport(true)} data-testid="leads-import-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#0f5c3d]/20 text-[#0b1a2e] text-sm font-medium hover:bg-[#f7fbf8]">
            <Upload className="w-4 h-4" /> Bulk import
          </button>
          <a href={leadsCsvExportUrl(filters.stage || '')} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#0f5c3d]/20 text-[#0b1a2e] text-sm font-medium hover:bg-[#f7fbf8]"
            data-testid="leads-export-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
          <button onClick={() => setShowNew(true)} data-testid="leads-new-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold hover:bg-[#0b4a30]">
            <Plus className="w-4 h-4" /> New lead
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-6" data-testid="leads-stats">
          <StatCard label="Total" value={stats.total} tone="dark" />
          <StatCard label="Open" value={stats.open} tone="emerald" />
          <StatCard label="Won" value={stats.won} tone="emerald-dark" />
          <StatCard label="Conversion" value={`${stats.conversion_pct}%`} tone="amber" />
          {STAGES.filter((s) => s !== 'won' && s !== 'lost').map((s) => (
            <StatCard key={s} label={STAGE_META[s].label} value={stats.by_stage[s]?.count || 0} tone="slate" />
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white border border-black/5 rounded-2xl p-3 mb-5 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-full bg-[#f7fbf8] border border-[#0f5c3d]/15 p-1">
          <button onClick={() => setView('list')} data-testid="leads-view-list"
            className={`px-3 py-1 text-xs font-medium rounded-full ${view === 'list' ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/70 hover:bg-white'}`}>
            List
          </button>
          <button onClick={() => setView('kanban')} data-testid="leads-view-kanban"
            className={`px-3 py-1 text-xs font-medium rounded-full ${view === 'kanban' ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/70 hover:bg-white'}`}>
            Kanban
          </button>
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/40" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Search name, company, email…" data-testid="leads-search"
            className="w-full pl-9 pr-3 py-1.5 bg-[#f7fbf8] rounded-full text-sm border border-transparent focus:border-[#0f5c3d]/30 focus:outline-none" />
        </div>
        <select value={filters.stage} onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))}
          data-testid="leads-filter-stage"
          className="px-3 py-1.5 bg-[#f7fbf8] rounded-full text-sm border border-transparent focus:border-[#0f5c3d]/30 focus:outline-none">
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
        </select>
        <select value={filters.owner_id} onChange={(e) => setFilters((f) => ({ ...f, owner_id: e.target.value }))}
          data-testid="leads-filter-owner"
          className="px-3 py-1.5 bg-[#f7fbf8] rounded-full text-sm border border-transparent focus:border-[#0f5c3d]/30 focus:outline-none">
          <option value="">Any owner</option>
          {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
          data-testid="leads-filter-source"
          className="px-3 py-1.5 bg-[#f7fbf8] rounded-full text-sm border border-transparent focus:border-[#0f5c3d]/30 focus:outline-none">
          <option value="">Any source</option>
          {Object.entries(SOURCE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-16 text-center text-[#0b1a2e]/50 bg-white rounded-2xl">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading leads…
        </div>
      ) : leads.length === 0 ? (
        <EmptyState onNew={() => setShowNew(true)} onImport={() => setShowImport(true)} />
      ) : view === 'list' ? (
        <ListView leads={leads} userLookup={userLookup} onSelect={setSelectedId} />
      ) : (
        <KanbanView leads={leads} userLookup={userLookup} onSelect={setSelectedId} onChanged={reload} />
      )}

      {/* Detail drawer */}
      {selectedId && (
        <LeadDetailDrawer
          leadId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={reload}
          users={users}
          depts={depts}
        />
      )}

      {/* New lead modal */}
      {showNew && (
        <NewLeadModal onClose={() => setShowNew(false)} onCreated={onCreated} users={users} />
      )}

      {/* Bulk import modal */}
      {showImport && (
        <BulkImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); reload(); }} />
      )}
    </div>
  );
};

// ============================================================
// Sub-components
// ============================================================

const StatCard = ({ label, value, tone = 'dark' }) => {
  const tones = {
    'dark': 'bg-[#0b1a2e] text-white',
    'emerald': 'bg-emerald-50 text-emerald-900',
    'emerald-dark': 'bg-[#0f5c3d] text-white',
    'amber': 'bg-amber-50 text-amber-900',
    'slate': 'bg-white border border-black/5 text-[#0b1a2e]',
  };
  return (
    <div className={`${tones[tone]} rounded-xl p-3`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-0.5">{value}</div>
    </div>
  );
};

const EmptyState = ({ onNew, onImport }) => (
  <div className="p-16 bg-white rounded-2xl text-center border border-black/5" data-testid="leads-empty">
    <div className="w-14 h-14 rounded-full bg-[#f7fbf8] flex items-center justify-center mx-auto mb-4">
      <UserIcon className="w-6 h-6 text-[#0f5c3d]" />
    </div>
    <div className="text-lg font-medium text-[#0b1a2e] mb-1">No leads yet</div>
    <p className="text-sm text-[#0b1a2e]/60 mb-5 max-w-md mx-auto">
      Start by importing a spreadsheet of existing contacts, or add one lead manually. Website contact form submissions will auto-populate here.
    </p>
    <div className="flex gap-2 justify-center">
      <button onClick={onImport} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white border border-[#0f5c3d]/20 text-sm font-medium hover:bg-[#f7fbf8]">
        <Upload className="w-4 h-4" /> Bulk import
      </button>
      <button onClick={onNew} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">
        <Plus className="w-4 h-4" /> New lead
      </button>
    </div>
  </div>
);

const ListView = ({ leads, userLookup, onSelect }) => (
  <div className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid="leads-list">
    <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold border-b border-black/5">
      <div className="col-span-4">Lead</div>
      <div className="col-span-2">Stage</div>
      <div className="col-span-2">Value</div>
      <div className="col-span-2">Owner</div>
      <div className="col-span-2">Last activity</div>
    </div>
    {leads.map((l) => (
      <button key={l.id} onClick={() => onSelect(l.id)} data-testid="lead-row"
        className="w-full grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-3 border-b border-black/5 last:border-b-0 hover:bg-[#f7fbf8] text-left">
        <div className="md:col-span-4">
          <div className="text-sm font-semibold text-[#0b1a2e]">{l.name}</div>
          <div className="text-xs text-[#0b1a2e]/60 flex flex-wrap items-center gap-2 mt-0.5">
            {l.company && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{l.company}</span>}
            {l.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</span>}
            {l.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</span>}
          </div>
        </div>
        <div className="md:col-span-2">
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_META[l.stage]?.color || 'bg-slate-100'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${STAGE_META[l.stage]?.dot || 'bg-slate-400'}`} />
            {STAGE_META[l.stage]?.label || l.stage}
          </span>
          {l.source && (
            <span className={`ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded ${SOURCE_META[l.source]?.color || 'bg-slate-100'}`}>
              {SOURCE_META[l.source]?.label || l.source}
            </span>
          )}
        </div>
        <div className="md:col-span-2 text-sm text-[#0b1a2e]">
          {fmtCurrency(l.value, l.currency)}
        </div>
        <div className="md:col-span-2 text-sm text-[#0b1a2e]/70 truncate">
          {l.owner_id ? (userLookup[l.owner_id]?.name || userLookup[l.owner_id]?.email || '—') : <span className="italic text-[#0b1a2e]/40">Unassigned</span>}
        </div>
        <div className="md:col-span-2 text-xs text-[#0b1a2e]/50 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {fmt(l.last_activity_at || l.created_at)}
        </div>
      </button>
    ))}
  </div>
);

const KanbanView = ({ leads, userLookup, onSelect, onChanged }) => {
  const grouped = useMemo(() => {
    const m = {}; STAGES.forEach((s) => { m[s] = []; });
    leads.forEach((l) => { (m[l.stage] = m[l.stage] || []).push(l); });
    return m;
  }, [leads]);

  const [dragId, setDragId] = useState(null);

  const setStage = async (leadId, stage) => {
    try {
      await updateLead(leadId, { stage });
      toast({ title: 'Stage updated' });
      onChanged();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const dropOn = async (stage) => {
    if (!dragId) return;
    await setStage(dragId, stage);
    setDragId(null);
  };

  return (
    <div
      className="flex md:grid md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-2 -mx-4 px-4 md:mx-0 md:px-0"
      data-testid="leads-kanban"
    >
      {STAGES.map((s) => (
        <div key={s}
          onDragOver={(e) => { if (dragId) e.preventDefault(); }}
          onDrop={() => dropOn(s)}
          className={`shrink-0 w-[78vw] md:w-auto snap-start bg-[#f7fbf8] rounded-xl p-2 min-h-[240px] ${dragId ? 'ring-2 ring-transparent hover:ring-[#0f5c3d]/30' : ''}`}>
          <div className={`px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded ${STAGE_META[s].color} mb-2 flex items-center justify-between`}>
            <span>{STAGE_META[s].label}</span>
            <span className="opacity-70">{grouped[s]?.length || 0}</span>
          </div>
          <div className="space-y-2">
            {(grouped[s] || []).map((l) => (
              <div key={l.id}
                draggable
                onDragStart={() => setDragId(l.id)}
                onDragEnd={() => setDragId(null)}
                data-testid="lead-kanban-card"
                className="bg-white rounded-lg p-2.5 shadow-sm border border-black/5 hover:border-[#0f5c3d]/30">
                <button onClick={() => onSelect(l.id)} className="w-full text-left">
                  <div className="text-sm font-medium text-[#0b1a2e] truncate">{l.name}</div>
                  {l.company && <div className="text-xs text-[#0b1a2e]/60 truncate">{l.company}</div>}
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs font-semibold text-[#0f5c3d]">{fmtCurrency(l.value, l.currency)}</span>
                    {l.owner_id && (
                      <div className="w-5 h-5 rounded-full bg-[#0f5c3d] text-white text-[9px] flex items-center justify-center font-semibold" title={userLookup[l.owner_id]?.name}>
                        {(userLookup[l.owner_id]?.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                </button>
                {/* Touch-friendly stage picker — visible on mobile only, hidden on desktop where drag works */}
                <select
                  value={s}
                  onChange={(e) => { e.stopPropagation(); if (e.target.value !== s) setStage(l.id, e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="lead-kanban-stage-select"
                  className="md:hidden mt-2 w-full text-[11px] px-2 py-1 border border-[#0f5c3d]/20 rounded bg-white text-[#0b1a2e]"
                  aria-label={`Change stage of ${l.name}`}
                >
                  {STAGES.map((opt) => <option key={opt} value={opt}>Move to · {STAGE_META[opt].label}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// New Lead Modal
// ============================================================

const NewLeadModal = ({ onClose, onCreated, users }) => {
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    stage: 'new', source: 'manual', value: '', currency: 'USD',
    owner_id: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) return toast({ title: 'Name is required', variant: 'destructive' });
    setSaving(true);
    try {
      const payload = { ...form };
      payload.value = payload.value ? parseFloat(payload.value) : null;
      if (!payload.email) delete payload.email;
      if (!payload.owner_id) delete payload.owner_id;
      const l = await createLead(payload);
      toast({ title: 'Lead created' });
      onCreated(l);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="new-lead-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-black/5 bg-[#0b1a2e] text-white flex items-center justify-between">
          <div className="text-lg font-medium">New lead</div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="new-lead-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="new-lead-name" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Company"><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Email"><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email"
              data-testid="new-lead-email" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Stage">
              <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                {STAGES.map((s) => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
              </select>
            </Field>
            <Field label="Source">
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                {Object.entries(SOURCE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
            </Field>
            <Field label="Value">
              <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} type="number"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </Field>
            <Field label="Currency">
              <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 3) })}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </Field>
            <Field label="Owner" wide>
              <select value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                data-testid="new-lead-owner"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#0b1a2e]/70 hover:text-[#0b1a2e]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="new-lead-submit"
            className="px-4 py-1.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold hover:bg-[#0b4a30] disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Create lead'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, wide }) => (
  <label className={`block ${wide ? 'col-span-2' : ''}`}>
    <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-1">{label}</div>
    {children}
  </label>
);

// ============================================================
// Bulk Import Wizard
// ============================================================

const BulkImportModal = ({ onClose, onDone }) => {
  const [step, setStep] = useState(1); // 1=paste/upload, 2=preview, 3=result
  const [csvText, setCsvText] = useState('');
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [dedupe, setDedupe] = useState(true);
  const [result, setResult] = useState(null);
  const [committing, setCommitting] = useState(false);

  const parse = async () => {
    if (!csvText.trim()) return;
    setParsing(true);
    try {
      const r = await parseLeadsCsv(csvText);
      setRows(r.rows || []);
      setStep(2);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Parse failed', variant: 'destructive' });
    } finally { setParsing(false); }
  };

  const commit = async (dryRun = false) => {
    setCommitting(true);
    try {
      const r = await importLeads({ rows, dedupe, dry_run: dryRun });
      if (dryRun) {
        toast({ title: `Dry run: ${r.inserted} would import, ${r.skipped_duplicates} dupes` });
      } else {
        setResult(r);
        setStep(3);
      }
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Import failed', variant: 'destructive' });
    } finally { setCommitting(false); }
  };

  const onFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="import-modal">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-black/5 bg-[#0b1a2e] text-white flex items-center justify-between">
          <div>
            <div className="text-lg font-medium">Bulk import leads</div>
            <div className="text-[11px] text-white/60 mt-0.5">Step {step} of 3 · {step === 1 ? 'Paste or upload CSV' : step === 2 ? 'Review before importing' : 'Done'}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {step === 1 && (
            <div className="space-y-4">
              <div className="p-3 bg-[#f7fbf8] border border-[#0f5c3d]/15 rounded-lg text-sm text-[#0b1a2e]/80">
                <div className="font-medium text-[#0b1a2e] mb-1">Recommended CSV format</div>
                <code className="text-[11px] block bg-white px-2 py-1.5 rounded border border-black/5 overflow-x-auto whitespace-pre">
                  name, company, email, phone, source, stage, value, currency, tags, notes, owner_email
                </code>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <a href={leadsCsvTemplateUrl} target="_blank" rel="noreferrer" className="text-[#0f5c3d] font-medium hover:underline inline-flex items-center gap-1">
                    <Download className="w-3 h-3" /> Download starter template
                  </a>
                  <span className="text-[#0b1a2e]/40">·</span>
                  <span className="text-[#0b1a2e]/60">Only <b>name</b> is required. Duplicates by email/phone are auto-skipped.</span>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-1">Upload CSV file</label>
                <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])}
                  data-testid="import-file"
                  className="text-sm" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-1">Or paste CSV directly</label>
                <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={8}
                  data-testid="import-textarea"
                  placeholder="name,company,email,phone,source,stage,value,currency,tags,notes,owner_email"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={dedupe} onChange={(e) => setDedupe(e.target.checked)} className="w-4 h-4 accent-[#0f5c3d]" />
                Skip duplicates (match by email or phone)
              </label>
            </div>
          )}
          {step === 2 && (
            <div>
              <div className="text-sm text-[#0b1a2e]/70 mb-2">Preview — <b>{rows.length}</b> rows detected. Review, then import.</div>
              <div className="border border-black/5 rounded-lg overflow-auto max-h-[50vh]">
                <table className="w-full text-xs" data-testid="import-preview">
                  <thead className="bg-[#f7fbf8] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Name</th>
                      <th className="text-left px-2 py-1.5">Company</th>
                      <th className="text-left px-2 py-1.5">Email</th>
                      <th className="text-left px-2 py-1.5">Phone</th>
                      <th className="text-left px-2 py-1.5">Stage</th>
                      <th className="text-left px-2 py-1.5">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 100).map((r, i) => (
                      <tr key={i} className="border-t border-black/5">
                        <td className="px-2 py-1 font-medium">{r.name}</td>
                        <td className="px-2 py-1">{r.company}</td>
                        <td className="px-2 py-1 text-[#0b1a2e]/70">{r.email || '—'}</td>
                        <td className="px-2 py-1 text-[#0b1a2e]/70">{r.phone}</td>
                        <td className="px-2 py-1">{r.stage}</td>
                        <td className="px-2 py-1">{r.value != null ? r.value : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 100 && (
                  <div className="p-2 text-xs text-[#0b1a2e]/50 text-center">Showing first 100 of {rows.length}</div>
                )}
              </div>
            </div>
          )}
          {step === 3 && result && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <ChevronRight className="w-7 h-7 text-emerald-700" />
              </div>
              <div className="text-2xl font-medium text-[#0b1a2e]">Import complete</div>
              <div className="mt-3 grid grid-cols-3 gap-3 max-w-md mx-auto">
                <StatCard label="Imported" value={result.inserted} tone="emerald-dark" />
                <StatCard label="Duplicates" value={result.skipped_duplicates} tone="amber" />
                <StatCard label="Invalid" value={result.invalid} tone="slate" />
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-black/5 bg-[#f7fbf8] flex justify-between items-center">
          <div className="text-xs text-[#0b1a2e]/60">
            {step === 2 && `${rows.length} rows ready`}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <>
                <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#0b1a2e]/70 hover:text-[#0b1a2e]">Cancel</button>
                <button onClick={parse} disabled={!csvText.trim() || parsing} data-testid="import-parse"
                  className="px-4 py-1.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">
                  {parsing ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Preview'}
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <button onClick={() => setStep(1)} className="px-3 py-1.5 text-sm text-[#0b1a2e]/70 hover:text-[#0b1a2e]">Back</button>
                <button onClick={() => commit(true)} disabled={committing}
                  className="px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-sm">Dry run</button>
                <button onClick={() => commit(false)} disabled={committing} data-testid="import-commit"
                  className="px-4 py-1.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">
                  {committing ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `Import ${rows.length} leads`}
                </button>
              </>
            )}
            {step === 3 && (
              <button onClick={onDone} className="px-4 py-1.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Lead Detail Drawer
// ============================================================

const LeadDetailDrawer = ({ leadId, onClose, onChanged, users, depts }) => {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [fu, setFu] = useState({ due_at: '', note: '', assignee_id: '' });
  const [convertOpen, setConvertOpen] = useState(false);
  const [convert, setConvert] = useState({ priority: 'medium', department_ids: [], assignee_ids: [] });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const l = await getLead(leadId);
      setLead(l);
    } catch {
      toast({ title: 'Failed to load lead', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const userLookup = useMemo(() => {
    const m = {}; users.forEach((u) => { m[u.user_id] = u; }); return m;
  }, [users]);

  const patch = async (fields) => {
    setSaving(true);
    try {
      const updated = await updateLead(leadId, fields);
      setLead((l) => ({ ...l, ...updated }));
      onChanged();
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    try {
      const a = await addLeadNote(leadId, note.trim());
      setLead((l) => ({ ...l, activities: [a, ...(l.activities || [])] }));
      setNote('');
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const submitFollowup = async () => {
    if (!fu.due_at) return toast({ title: 'Pick a date/time', variant: 'destructive' });
    try {
      await addLeadFollowup(leadId, { ...fu, create_task: true });
      setFu({ due_at: '', note: '', assignee_id: '' });
      toast({ title: 'Follow-up scheduled — added to Calendar' });
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const toggleFollowup = async (id, done) => {
    try {
      await updateLeadFollowup(leadId, id, { done });
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const removeFollowup = async (id) => {
    try { await deleteLeadFollowup(leadId, id); load(); } catch {}
  };

  const submitConvert = async () => {
    try {
      const t = await convertLeadToTicket(leadId, convert);
      toast({ title: `Converted to ticket ${t.ticket_no}` });
      setConvertOpen(false);
      load();
      onChanged();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const doDelete = async () => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    try {
      await deleteLead(leadId);
      onChanged();
      onClose();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Delete failed', variant: 'destructive' }); }
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadFile(file);
      await addLeadFile(leadId, {
        url: up.url,
        filename: file.name,
        size: up.size,
        mime_type: up.content_type,
      });
      toast({ title: 'File attached' });
      load();
    } catch { toast({ title: 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); }
  };

  const removeFile = async (fid) => {
    try { await deleteLeadFile(leadId, fid); load(); } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/50" data-testid="lead-drawer">
      <div className="bg-white w-full max-w-3xl h-full overflow-hidden flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-black/5 bg-[#0b1a2e] text-white flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Lead</div>
            <div className="text-lg font-medium truncate">{lead?.name || '…'}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded" data-testid="lead-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {loading || !lead ? (
          <div className="p-16 text-center text-[#0b1a2e]/50 flex-1">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
          </div>
        ) : (
          <div className="flex-1 overflow-auto grid grid-cols-1 lg:grid-cols-3">
            {/* Left: activities / notes / follow-ups / files */}
            <div className="lg:col-span-2 p-5 border-r border-black/5">
              {/* Actions */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={() => setConvertOpen((o) => !o)} data-testid="lead-convert-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold hover:bg-[#0b4a30]">
                  <TicketIcon className="w-3.5 h-3.5" /> Convert to ticket
                </button>
                <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-xs font-medium hover:bg-[#f7fbf8] cursor-pointer">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                  {uploading ? 'Uploading…' : 'Attach file'}
                  <input type="file" hidden onChange={(e) => uploadAttachment(e.target.files?.[0])} data-testid="lead-file-input" />
                </label>
                <button onClick={doDelete} data-testid="lead-delete-btn"
                  className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-red-300 text-red-700 text-xs font-medium hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>

              {convertOpen && (
                <div className="mb-5 p-3 bg-[#f7fbf8] border border-[#0f5c3d]/15 rounded-lg" data-testid="lead-convert-panel">
                  <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2">Convert to ticket</div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <select value={convert.priority} onChange={(e) => setConvert({ ...convert, priority: e.target.value })}
                      className="px-2 py-1.5 border border-slate-300 rounded text-sm">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <select multiple value={convert.department_ids}
                      onChange={(e) => setConvert({ ...convert, department_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                      className="px-2 py-1.5 border border-slate-300 rounded text-sm h-16">
                      {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setConvertOpen(false)} className="px-3 py-1 text-xs">Cancel</button>
                    <button onClick={submitConvert} data-testid="lead-convert-submit"
                      className="px-3 py-1 bg-[#0f5c3d] text-white rounded-full text-xs font-semibold">Convert</button>
                  </div>
                </div>
              )}

              {/* Follow-ups */}
              <div className="mb-6" data-testid="lead-followups">
                <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> Follow-ups
                </div>
                <div className="space-y-1.5 mb-3">
                  {(lead.followups || []).length === 0 ? (
                    <div className="text-xs text-[#0b1a2e]/50 italic">No follow-ups scheduled yet.</div>
                  ) : lead.followups.map((f) => (
                    <div key={f.id} className={`flex items-center gap-2 p-2 rounded ${f.done ? 'bg-emerald-50' : 'bg-[#f7fbf8]'}`}>
                      <input type="checkbox" checked={!!f.done} onChange={(e) => toggleFollowup(f.id, e.target.checked)}
                        className="w-4 h-4 accent-[#0f5c3d]" />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${f.done ? 'line-through text-[#0b1a2e]/50' : 'text-[#0b1a2e]'}`}>
                          {f.note || 'Follow-up'}
                        </div>
                        <div className="text-[10px] text-[#0b1a2e]/60">
                          {fmt(f.due_at)}
                          {f.assignee_id && ` · ${userLookup[f.assignee_id]?.name || 'staff'}`}
                          {f.task_id && ' · On calendar'}
                        </div>
                      </div>
                      <button onClick={() => removeFollowup(f.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <input type="datetime-local" value={fu.due_at} onChange={(e) => setFu({ ...fu, due_at: e.target.value })}
                    data-testid="followup-date" className="col-span-1 sm:col-span-2 px-2 py-1.5 border border-slate-300 rounded text-sm" />
                  <select value={fu.assignee_id} onChange={(e) => setFu({ ...fu, assignee_id: e.target.value })}
                    className="px-2 py-1.5 border border-slate-300 rounded text-sm">
                    <option value="">Auto (owner)</option>
                    {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                  </select>
                  <button onClick={submitFollowup} data-testid="followup-add"
                    className="px-3 py-1.5 bg-[#0f5c3d] text-white rounded text-sm font-semibold">Schedule</button>
                  <input value={fu.note} onChange={(e) => setFu({ ...fu, note: e.target.value })}
                    placeholder="Note (e.g., call to discuss pricing)"
                    className="col-span-1 sm:col-span-4 px-2 py-1.5 border border-slate-300 rounded text-sm" />
                </div>
              </div>

              {/* Files */}
              {(lead.files || []).length > 0 && (
                <div className="mb-6" data-testid="lead-files">
                  <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2 flex items-center gap-1">
                    <Paperclip className="w-3.5 h-3.5" /> Files ({lead.files.length})
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {lead.files.map((f) => (
                      <div key={f.id} className="group relative bg-[#f7fbf8] border border-black/5 rounded-lg overflow-hidden">
                        {f.mime_type?.startsWith('image/') ? (
                          <a href={f.url} target="_blank" rel="noreferrer">
                            <img src={f.url} alt={f.filename} className="w-full h-24 object-cover" />
                          </a>
                        ) : (
                          <a href={f.url} target="_blank" rel="noreferrer" className="block p-3 text-center">
                            <FileText className="w-6 h-6 mx-auto mb-1 text-[#0b1a2e]/40" />
                            <div className="text-[11px] text-[#0b1a2e] truncate">{f.filename}</div>
                          </a>
                        )}
                        <button onClick={() => removeFile(f.id)}
                          className="absolute top-1 right-1 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100">
                          <Trash2 className="w-3 h-3 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add note */}
              <div className="mb-4">
                <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2">Add note</div>
                <div className="flex gap-2">
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    data-testid="lead-note-input"
                    placeholder="Log a call, meeting notes, or a next step…"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm" />
                  <button onClick={submitNote} data-testid="lead-note-submit"
                    className="px-3 py-2 bg-[#0f5c3d] text-white rounded text-sm font-semibold">Log</button>
                </div>
              </div>

              {/* Timeline */}
              <div data-testid="lead-timeline">
                <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-2">Activity</div>
                <div className="space-y-2">
                  {(lead.activities || []).length === 0 ? (
                    <div className="text-xs text-[#0b1a2e]/50 italic">No activity yet.</div>
                  ) : lead.activities.map((a) => (
                    <div key={a.id} className="flex gap-2 text-sm">
                      <div className="w-6 h-6 rounded-full bg-[#0f5c3d] text-white text-[10px] flex items-center justify-center flex-shrink-0 font-semibold mt-0.5">
                        {(a.actor_name || 'S').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#0b1a2e]/60">
                          <span className="font-semibold text-[#0b1a2e]">{a.actor_name}</span> · <span className="uppercase text-[10px] tracking-wider">{a.kind}</span> · {fmt(a.created_at)}
                        </div>
                        <div className="text-sm text-[#0b1a2e] whitespace-pre-wrap">{a.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: meta */}
            <div className="p-5 bg-[#f7fbf8] space-y-3">
              <MetaField label="Stage">
                <select value={lead.stage} onChange={(e) => patch({ stage: e.target.value })}
                  data-testid="lead-stage-select"
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1 font-medium">
                  {STAGES.map((s) => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
                </select>
              </MetaField>
              <MetaField label="Source">
                <select value={lead.source} onChange={(e) => patch({ source: e.target.value })}
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1">
                  {Object.entries(SOURCE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
              </MetaField>
              <MetaField label="Owner">
                <select value={lead.owner_id || ''} onChange={(e) => patch({ owner_id: e.target.value || null })}
                  data-testid="lead-owner-select"
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1">
                  <option value="">— Unassigned —</option>
                  {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                </select>
              </MetaField>
              <MetaField label="Value">
                <div className="flex gap-1">
                  <input type="number" defaultValue={lead.value ?? ''} onBlur={(e) => patch({ value: e.target.value ? parseFloat(e.target.value) : null })}
                    className="flex-1 text-sm bg-white border border-slate-300 rounded px-2 py-1" />
                  <input type="text" defaultValue={lead.currency} onBlur={(e) => patch({ currency: e.target.value.toUpperCase().slice(0, 3) })}
                    className="w-14 text-sm bg-white border border-slate-300 rounded px-2 py-1 uppercase" />
                </div>
              </MetaField>
              <MetaField label="Email">
                <input type="email" defaultValue={lead.email || ''} onBlur={(e) => patch({ email: e.target.value || null })}
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1" />
              </MetaField>
              <MetaField label="Phone">
                <input defaultValue={lead.phone || ''} onBlur={(e) => patch({ phone: e.target.value })}
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1" />
              </MetaField>
              <MetaField label="Company">
                <input defaultValue={lead.company || ''} onBlur={(e) => patch({ company: e.target.value })}
                  className="w-full text-sm bg-white border border-slate-300 rounded px-2 py-1" />
              </MetaField>
              <MetaField label="Created">
                <div className="text-xs text-[#0b1a2e]/70">{fmt(lead.created_at)}</div>
              </MetaField>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MetaField = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-1">{label}</div>
    {children}
  </div>
);

export default LeadsPage;
